import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { toAppError } from './errors';

/**
 * The one HTTP client (docs/07-implementation-roadmap.md F0.5).
 *
 * The types are generated from the backend's OpenAPI baseline; the request
 * layer is hand-written and small on purpose — a full generated SDK would hide
 * the auth, refresh and correlation-ID behaviour this file exists to own.
 */

/**
 * The API is wherever the app itself is served from (docs/07 §1.1) — that is
 * the whole point of the same-origin deployment, and it is why there is
 * deliberately no environment variable here: the build made for staging is the
 * artifact promoted to production.
 *
 * Every path in `schema.d.ts` already carries `/api/v1`, so the origin is all
 * that is needed. It is spelled out rather than left empty because `fetch`
 * outside a browser (the component-test runner) cannot resolve a relative URL,
 * and a client that only works in one of the two environments is a client the
 * tests cannot vouch for.
 */
const BASE_URL = typeof window === 'undefined' ? '' : window.location.origin;

/**
 * The access token lives in memory only — never `localStorage`, never a cookie
 * this code can read (docs/04 §1.1, docs/90 §3). F1 owns the session; F0 only
 * has to make sure there is exactly one place the token is read from, so the
 * refresh work in F1.3 has a single seam to hook.
 */
let accessTokenProvider: () => string | null = () => null;

export function setAccessTokenProvider(provider: () => string | null): void {
  accessTokenProvider = provider;
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = accessTokenProvider();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
};

/**
 * The other half of the seam: what to do when the API says the access token is
 * no longer good. `src/api/refresh.ts` registers the single-flight refresh here
 * (F1.3), and registers it rather than being imported so this module keeps
 * depending on nothing — the refresh needs the client, and a cycle between the
 * two is the kind of thing that works until a bundler reorders it.
 *
 * Resolves to a usable access token, or `null` when the session is over.
 */
let reauthenticate: (() => Promise<string | null>) | null = null;

export function setReauthenticator(refresh: () => Promise<string | null>): void {
  reauthenticate = refresh;
}

/**
 * A 401 means "this token is stale", not "you are logged out" — the refresh
 * cookie usually still has weeks left on it. So the request is replayed once
 * with a fresh token and the caller never learns it happened.
 *
 * The replay needs a copy of the request taken **before** it was sent: a request
 * that has been through `fetch` has had its body consumed, and a POST retried
 * without one is not the same call. Hence the clone in `onRequest`.
 *
 * The retry deliberately bypasses this middleware (it calls `fetch` directly),
 * which is what makes a refresh loop structurally impossible rather than
 * guarded against with a counter: a 401 on the replay is returned as-is.
 */
const AUTH_PATH_PREFIX = '/api/v1/auth/';
const replayableRequests = new Map<string, Request>();

const refreshMiddleware: Middleware = {
  onRequest({ request, schemaPath, id }) {
    // Nothing to replay for the auth endpoints themselves — a 401 from
    // /auth/login is a wrong password, and a 401 from /auth/refresh is the
    // session genuinely ending. Nothing to replay without a token either.
    if (schemaPath.startsWith(AUTH_PATH_PREFIX) || !accessTokenProvider()) return;
    replayableRequests.set(id, request.clone());
  },

  async onResponse({ response, id }) {
    const original = replayableRequests.get(id);
    replayableRequests.delete(id);

    if (response.status !== 401 || !original || !reauthenticate) return;

    const token = await reauthenticate();
    // The session is over (or the network is down). Let the 401 through: the
    // store has been cleared, and the shell answers it by routing to the login
    // screen with the attempted path kept (F1.4).
    if (!token) return;

    const headers = new Headers(original.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return globalThis.fetch(new Request(original, { headers }));
  },

  onError({ id }) {
    replayableRequests.delete(id);
  },
};

/**
 * Correlation IDs (docs/04 §8, docs/90 §2.5). The client generates one per
 * request so a staff bug report can be traced to a server log line. The API
 * reuses a caller-supplied `X-Correlation-Id` when it gets one, so the value
 * below is the value in Kibana.
 *
 * The response is where the ID has to be readable from — that's what the error
 * boundary shows and what a support code is copied from. The API does not echo
 * the header back yet (that lands with backend Sprint 7), so the ID we sent is
 * recorded per response here and used as the fallback. When the echo arrives,
 * `correlationIdFor` starts preferring it without anything else changing.
 */
const sentCorrelationIds = new Map<string, string>();
const responseCorrelationIds = new WeakMap<Response, string>();

function newCorrelationId(): string {
  // `crypto.randomUUID` only exists in a secure context. Staging and production
  // are HTTPS and localhost counts, so the fallback should never run in
  // practice — but a correlation ID is the thing you need precisely when
  // something unexpected is true about the environment, so it does not get to
  // throw.
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const correlationMiddleware: Middleware = {
  onRequest({ request, id }) {
    const correlationId = newCorrelationId();
    request.headers.set('X-Correlation-Id', correlationId);
    sentCorrelationIds.set(id, correlationId);
    return request;
  },
  onResponse({ response, id }) {
    const sent = sentCorrelationIds.get(id);
    sentCorrelationIds.delete(id);
    const echoed = response.headers.get('X-Correlation-Id');
    const correlationId = echoed ?? sent;
    if (correlationId) {
      responseCorrelationIds.set(response, correlationId);
      lastCorrelationId = correlationId;
      for (const listener of correlationListeners) listener();
    }
  },
  onError({ id }) {
    // A transport failure never produced a response, so nothing can read the ID
    // back off one. Drop it rather than leaking an entry per failed request.
    sentCorrelationIds.delete(id);
  },
};

/** The correlation ID that ties a response to its server-side log trail, if known. */
export function correlationIdFor(response: Response | undefined): string | undefined {
  return response ? responseCorrelationIds.get(response) : undefined;
}

/**
 * The most recent response's correlation ID, for the support affordance (F6.7).
 *
 * The error boundary can read the ID off the failure it caught. A staff member
 * reporting "this number looks wrong" has no failure to hand — nothing threw —
 * so the only ID available is the last one this client saw, which is the request
 * that drew what they are looking at.
 *
 * Deliberately last-seen rather than a history: a support code is only useful if
 * the person quoting it can say what they were doing, and a list of twenty ids
 * makes that harder, not easier.
 */
let lastCorrelationId: string | undefined;
const correlationListeners = new Set<() => void>();

export function subscribeToCorrelationId(listener: () => void): () => void {
  correlationListeners.add(listener);
  return () => correlationListeners.delete(listener);
}

export function getLastCorrelationId(): string | undefined {
  return lastCorrelationId;
}

export const api = createClient<paths>({
  baseUrl: BASE_URL,
  // Resolved per call rather than captured once. openapi-fetch otherwise binds
  // whatever `globalThis.fetch` was at module-evaluation time, which is a stale
  // reference to anything installed later — MSW's interceptor in component
  // tests being the case that shows up immediately, and any future transport
  // instrumentation the same way.
  fetch: (request) => globalThis.fetch(request),
  // The refresh token is an HttpOnly cookie, so it only travels if credentials
  // are sent. Same-origin would include it by default; this is explicit so a
  // future split-host deployment fails on CORS rather than by silently logging
  // everyone out 15 minutes after login (docs/07 §1.1).
  credentials: 'include',
});

// Registration order is load-bearing. `onRequest` runs in this order, so the
// refresh middleware clones a request that already carries its auth and
// correlation headers. `onResponse` runs in reverse, so the refresh middleware
// sees the 401 first and the correlation middleware then records the ID against
// whichever response — original or replayed — is the one the caller gets.
api.use(authMiddleware);
api.use(correlationMiddleware);
api.use(refreshMiddleware);

/** What every openapi-fetch call resolves to. */
type ApiResult<T> = { data?: T; error?: unknown; response: Response };

/**
 * Turns openapi-fetch's `{ data, error }` result into a value or a thrown
 * `AppError`.
 *
 * TanStack Query decides success by whether the promise rejected, so an
 * unwrapped `error` would be cached as a successful result. Every feature call
 * goes through here — that is also what guarantees the error envelope is
 * translated in exactly one place (F0.6).
 */
export async function unwrap<T>(call: Promise<ApiResult<T>>): Promise<T> {
  let result: ApiResult<T>;
  try {
    result = await call;
  } catch (cause) {
    // fetch rejected: the office lost its connection, or the request was
    // aborted. There is no response and no correlation ID.
    throw toAppError({ cause });
  }

  const { data, error, response } = result;
  if (error !== undefined || !response.ok) {
    throw toAppError({ body: error, response, correlationId: correlationIdFor(response) });
  }

  return data as T;
}
